import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import SharedModelView from "./SharedModelView";
import ContractsPage from "./ContractsPage";
import SharedInvestorView from "./SharedInvestorView";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { ScrollArea } from "./components/ui/scroll-area";
import { 
  TrendingUp, 
  Users, 
  Wallet, 
  PiggyBank, 
  Target, 
  BarChart3, 
  Download,
  Save,
  FolderOpen,
  Plus,
  Trash2,
  Calculator,
  Building2,
  Megaphone,
  DollarSign,
  Server,
  Briefcase,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  LogOut,
  User,
  Share2,
  Copy,
  Link,
  ExternalLink,
  Check,
  Eye,
  Lock,
  Unlock,
  UserPlus,
  Users2,
  Shield,
  Pencil,
  Settings,
  FileText
} from "lucide-react";
import { generatePDF } from "./lib/pdfExport";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  PieChart,
  Pie,
  Cell
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Separator } from "./components/ui/separator";
import { NotificationsBell } from "./NotificationsBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// Format currency
const formatCurrency = (value) => {
  const abs = Math.abs(value || 0);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1000000) return `${sign}€${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}€${(abs / 1000).toFixed(1)}K`;
  return `${sign}€${abs.toFixed(0)}`;
};

// Format number
const formatNumber = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value?.toFixed(0) || "0";
};

// Default model state
const getDefaultModel = () => ({
  name: "Nuovo Modello",
  business_metrics: {
    current_customers: 50,
    current_mrr: 25000,
    avg_users_per_customer: 5,
    price_per_user: 100,
    gross_margin: 80,
    annual_churn_rate: 5
  },
  growth_targets: {
    year1: { target_mrr: 50000, target_users_per_account: 6, target_price_per_user: 100, gross_margin: 80 },
    year2: { target_mrr: 120000, target_users_per_account: 7, target_price_per_user: 110, gross_margin: 80 },
    year3: { target_mrr: 300000, target_users_per_account: 8, target_price_per_user: 120, gross_margin: 80 }
  },
  sales_pipeline: {
    year1: { lead_to_discovery: 30, discovery_to_demo: 50, demo_to_customer: 25, sales_cycle_months: 2 },
    year2: { lead_to_discovery: 30, discovery_to_demo: 50, demo_to_customer: 25, sales_cycle_months: 2 },
    year3: { lead_to_discovery: 30, discovery_to_demo: 50, demo_to_customer: 25, sales_cycle_months: 2 }
  },
  sales_team: {
    year1: { leads_per_sales_rep: 50, commission_per_user: 50, ramp_time_months: 3, fixed_cost_per_rep: 4000 },
    year2: { leads_per_sales_rep: 50, commission_per_user: 50, ramp_time_months: 3, fixed_cost_per_rep: 4000 },
    year3: { leads_per_sales_rep: 50, commission_per_user: 50, ramp_time_months: 3, fixed_cost_per_rep: 4000 }
  },
  marketing_budget: {
    year1: { cost_per_lead: 50, event_costs: 2000, agency_costs: 1500, team_salaries: 8000 },
    year2: { cost_per_lead: 50, event_costs: 2000, agency_costs: 1500, team_salaries: 8000 },
    year3: { cost_per_lead: 50, event_costs: 2000, agency_costs: 1500, team_salaries: 8000 }
  },
  sales_marketing_cost: {
    year1: { sales_salaries: 15000, head_of_sales_salary: 10000, recruiting_costs: 2000, crm_tools: 500, marketing_team_salaries: 8000 },
    year2: { sales_salaries: 15000, head_of_sales_salary: 10000, recruiting_costs: 2000, crm_tools: 500, marketing_team_salaries: 8000 },
    year3: { sales_salaries: 15000, head_of_sales_salary: 10000, recruiting_costs: 2000, crm_tools: 500, marketing_team_salaries: 8000 }
  },
  operating_costs: {
    year1: { engineering_team: 30000, ai_infrastructure: 5000, customer_success: 8000, admin_legal: 3000, office_tools: 2000, custom_items: [] },
    year2: { engineering_team: 30000, ai_infrastructure: 5000, customer_success: 8000, admin_legal: 3000, office_tools: 2000, custom_items: [] },
    year3: { engineering_team: 30000, ai_infrastructure: 5000, customer_success: 8000, admin_legal: 3000, office_tools: 2000, custom_items: [] }
  },
  cash_funding: {
    starting_cash: 500000,
    investment_amount: 0,
    burn_target: 50000
  }
});

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, className = "" }) => (
  <Card className={`card-hover border-border/60 ${className}`} data-testid={`metric-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold font-data tracking-tight text-foreground">{value}</p>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          {Icon && (
            <div className="p-2 bg-secondary rounded-sm">
              <Icon className="h-5 w-5 text-primary" />
            </div>
          )}
          {trend && (
            <div className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
              {trend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {trendValue}
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Format number with Italian thousands separator (1000 -> 1.000)
const formatNumberInput = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/\./g, '').replace(',', '.')) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('it-IT', { maximumFractionDigits: 2 });
};

// Parse Italian formatted number back to float (1.000 -> 1000)
const parseNumberInput = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  // Remove thousand separators and convert comma to dot for decimals
  const cleanValue = value.toString().replace(/\./g, '').replace(',', '.');
  return parseFloat(cleanValue) || 0;
};

// Input Field Component with formatted numbers
const InputField = ({ label, value, onChange, prefix, suffix, type = "number", className = "", id }) => {
  const [displayValue, setDisplayValue] = useState(formatNumberInput(value));
  const [isFocused, setIsFocused] = useState(false);
  
  // Update display value when prop value changes (but not during editing)
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatNumberInput(value));
    }
  }, [value, isFocused]);
  
  const handleFocus = () => {
    setIsFocused(true);
    // Show raw number without formatting when focused for easier editing
    setDisplayValue(value?.toString() || '');
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    const parsedValue = parseNumberInput(displayValue);
    onChange(parsedValue);
    setDisplayValue(formatNumberInput(parsedValue));
  };
  
  const handleChange = (e) => {
    const inputValue = e.target.value;
    // Allow only numbers, dots and commas during input
    if (/^[\d.,]*$/.test(inputValue) || inputValue === '') {
      setDisplayValue(inputValue);
    }
  };
  
  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor={id} className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-data">
            {prefix}
          </span>
        )}
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`font-data h-10 ${prefix ? 'pl-8' : ''} ${suffix ? 'pr-12' : ''}`}
          data-testid={id}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-data">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};

// Section Header Component
const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-center gap-4 mb-6">
    <div className="p-3 bg-secondary rounded-sm">
      <Icon className="h-6 w-6 text-primary" />
    </div>
    <div>
      <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
      {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
    </div>
  </div>
);

// Year selector for per-year cost sections
const YearCostTabs = ({ value, onChange, onCopy }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="flex bg-muted rounded-md p-0.5" data-testid="year-cost-tabs">
      {["year1", "year2", "year3"].map((yr) => (
        <button
          key={yr}
          onClick={() => onChange(yr)}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
            value === yr 
              ? "bg-white text-foreground shadow-sm" 
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`cost-year-tab-${yr}`}
        >
          Anno {yr.replace("year", "")}
        </button>
      ))}
    </div>
    {onCopy && (
      <Button variant="ghost" size="sm" onClick={() => onCopy(value)} className="text-xs text-muted-foreground" data-testid="copy-year-costs-btn">
        Copia su tutti gli anni
      </Button>
    )}
  </div>
);

// ============== LOGIN PAGE ==============
const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError("Inserisci email e password"); return; }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      if (response.data.user) {
        toast.success(`Benvenuto, ${response.data.user.name}!`);
        const dest = '/contracts';
        navigate(dest, { state: { user: response.data.user } });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore di autenticazione";
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-md border-border/60">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto">
            <img src="/api/static/logo" alt="Aiclo" className="h-14 mx-auto" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">Gestionale</CardTitle>
            <CardDescription className="mt-2">
              Pianificazione finanziaria per startup SaaS
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <form onSubmit={handleEmailLogin} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className="text-sm">Email</Label>
              <Input
                id="login-email"
                type="email"
                placeholder="email@azienda.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="login-password" className="text-sm">Password</Label>
              <Input
                id="login-password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                data-testid="login-password-input"
              />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-btn">
              {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" /> : "Accedi"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><Separator /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-background px-3 text-muted-foreground">oppure</span></div>
          </div>

          <Button
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full h-11 gap-3"
            data-testid="google-login-btn"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Accedi con Google
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== AUTH CALLBACK ==============
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error("Sessione non valida");
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId
        });

        if (response.data.user) {
          toast.success(`Benvenuto, ${response.data.user.name}!`);
          const dest = '/contracts';
          navigate(dest, { state: { user: response.data.user } });
        } else {
          throw new Error("Dati utente mancanti");
        }
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Errore di autenticazione");
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground">Autenticazione in corso...</p>
      </div>
    </div>
  );
};

// ============== MAIN DASHBOARD ==============
const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isLoading, setIsLoading] = useState(!location.state?.user);
  const [model, setModel] = useState(getDefaultModel());
  const [metrics, setMetrics] = useState(null);
  const [savedModels, setSavedModels] = useState([]);
  const [currentModelId, setCurrentModelId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  // User management state (admin only)
  const [managedUsers, setManagedUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: "", password: "", name: "", role: "sales" });
  const [creatingUser, setCreatingUser] = useState(false);
  // Product management state (admin only)
  const [products, setProducts] = useState([]);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", monthly_price: "" });
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [settingsSection, setSettingsSection] = useState("users");
  const [showSavedModels, setShowSavedModels] = useState(false);
  const [dashboardYear, setDashboardYear] = useState("year1");
  const [costYear, setCostYear] = useState("year1");

  // Get metrics for the selected dashboard year
  const ym = (dashboardYear === "consolidated" ? metrics?.year3 : metrics?.[dashboardYear]) || metrics?.year1 || null;
  const ymProjections = dashboardYear === "consolidated" 
    ? metrics?.consolidated?.monthly_projections || []
    : ym?.monthly_projections || [];
  // BEP: always global (across all 36 months), independent of selected year
  const ymBep = metrics?.consolidated?.bep;
  // Runway: always from year1 (starting cash / initial burn rate)
  const ymRunway = metrics?.year1?.cash;
  // Cost year metrics (for input tab indicators)
  const cym = metrics?.[costYear] || metrics?.year1 || null;

  // Check authentication
  useEffect(() => {
    if (location.state?.user) {
      if (location.state.user.role === 'sales' || location.state.user.role === 'csm') {
        navigate('/contracts', { state: { user: location.state.user }, replace: true });
        return;
      }
      setUser(location.state.user);
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (response.data.role === 'sales' || response.data.role === 'csm') {
          navigate('/contracts', { replace: true });
          return;
        }
        setUser(response.data);
      } catch (error) {
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  // Calculate metrics on model change
  const calculateMetrics = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/calculate`, model);
      setMetrics(response.data.metrics);
    } catch (error) {
      console.error("Errore nel calcolo:", error);
    }
  }, [model]);

  // Debounced calculation
  useEffect(() => {
    const timer = setTimeout(() => {
      calculateMetrics();
    }, 300);
    return () => clearTimeout(timer);
  }, [calculateMetrics]);

  // Load saved models
  const loadSavedModels = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/models`);
      setSavedModels(response.data);
    } catch (error) {
      console.error("Errore caricamento modelli:", error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadSavedModels();
    }
  }, [user, loadSavedModels]);

  // Save model
  const saveModel = async () => {
    setIsSaving(true);
    try {
      if (currentModelId) {
        await axios.put(`${API}/models/${currentModelId}`, model);
        toast.success("Modello aggiornato con successo!");
      } else {
        const response = await axios.post(`${API}/models`, model);
        setCurrentModelId(response.data.model.id);
        toast.success("Modello salvato con successo!");
      }
      loadSavedModels();
    } catch (error) {
      toast.error("Errore nel salvataggio del modello");
    }
    setIsSaving(false);
  };

  // Load model
  const loadModel = async (modelId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/models/${modelId}`);
      setModel(response.data.model);
      setMetrics(response.data.metrics);
      setCurrentModelId(modelId);
      setShowSavedModels(false);
      // Load share info from model data
      const m = response.data.model;
      if (m.is_shared && m.share_token) {
        setShareInfo({ share_token: m.share_token, is_shared: true, has_password: !!m.share_password });
      } else {
        setShareInfo(null);
      }
      toast.success("Modello caricato!");
    } catch (error) {
      toast.error("Errore nel caricamento del modello");
    }
    setIsLoading(false);
  };

  // Delete model
  const deleteModel = async (modelId) => {
    try {
      await axios.delete(`${API}/models/${modelId}`);
      if (currentModelId === modelId) {
        setCurrentModelId(null);
        setModel(getDefaultModel());
      }
      loadSavedModels();
      toast.success("Modello eliminato");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Export CSV
  const exportCSV = async () => {
    if (!currentModelId) {
      toast.error("Salva il modello prima di esportare");
      return;
    }
    try {
      window.open(`${API}/models/${currentModelId}/export`, '_blank');
      toast.success("Export avviato!");
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  // Export PDF
  const exportPDF = async () => {
    if (!metrics) {
      toast.error("Nessun dato da esportare. Calcola prima il modello.");
      return;
    }
    setIsExportingPdf(true);
    try {
      await generatePDF(model, metrics, metrics?.consolidated?.bep);
      toast.success("PDF generato con successo!");
    } catch (error) {
      console.error("Errore generazione PDF:", error);
      toast.error("Errore nella generazione del PDF");
    }
    setIsExportingPdf(false);
  };

  // New model
  const newModel = () => {
    setModel(getDefaultModel());
    setCurrentModelId(null);
    setMetrics(null);
    setShareInfo(null);
    toast.info("Nuovo modello creato");
  };

  // Share state
  const [shareInfo, setShareInfo] = useState(null); // { share_token, is_shared, has_password }
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [sharePassword, setSharePassword] = useState("");

  const shareUrl = shareInfo?.share_token 
    ? `${window.location.origin}/shared/${shareInfo.share_token}` 
    : null;

  // Enable sharing
  const enableSharing = async () => {
    if (!currentModelId) {
      toast.error("Salva il modello prima di condividerlo");
      return;
    }
    setSharingLoading(true);
    try {
      const body = sharePassword ? { password: sharePassword } : {};
      const response = await axios.post(`${API}/models/${currentModelId}/share`, body);
      setShareInfo(response.data);
      setSharePassword("");
      toast.success("Link di condivisione creato!");
    } catch (error) {
      toast.error("Errore nella condivisione");
    }
    setSharingLoading(false);
  };

  // Revoke sharing
  const revokeSharing = async () => {
    if (!currentModelId) return;
    setSharingLoading(true);
    try {
      await axios.delete(`${API}/models/${currentModelId}/share`);
      setShareInfo(null);
      setSharePassword("");
      toast.success("Condivisione revocata");
    } catch (error) {
      toast.error("Errore nella revoca");
    }
    setSharingLoading(false);
  };

  // Copy share link
  const copyShareLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiato!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // ===== User Management (Admin only) =====
  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setManagedUsers(response.data.users || []);
    } catch (e) { /* ignore for non-admin */ }
  };

  const createNewUser = async () => {
    if (!newUserForm.email || !newUserForm.password || !newUserForm.name) {
      toast.error("Compila tutti i campi"); return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, newUserForm);
      toast.success("Utente creato!");
      setNewUserForm({ email: "", password: "", name: "", role: "sales" });
      setShowCreateUser(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    }
    setCreatingUser(false);
  };

  const deleteUserById = async (userId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Utente eliminato");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // ===== Product Management (Admin only) =====
  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.products || []);
    } catch (e) { /* ignore */ }
  };

  const createProduct = async () => {
    if (!newProduct.name.trim() || !newProduct.monthly_price) {
      toast.error("Inserisci nome e prezzo"); return;
    }
    setSavingProduct(true);
    try {
      await axios.post(`${API}/products`, { name: newProduct.name, monthly_price: parseFloat(newProduct.monthly_price) });
      toast.success("Prodotto creato!");
      setNewProduct({ name: "", monthly_price: "" });
      setShowCreateProduct(false);
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    }
    setSavingProduct(false);
  };

  const saveEditProduct = async () => {
    if (!editingProduct) return;
    setSavingProduct(true);
    try {
      await axios.put(`${API}/products/${editingProduct.product_id}`, {
        name: editingProduct.name,
        monthly_price: parseFloat(editingProduct.monthly_price)
      });
      toast.success("Prodotto aggiornato!");
      setEditingProduct(null);
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiornamento");
    }
    setSavingProduct(false);
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo prodotto?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Prodotto eliminato");
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      toast.success("Logout effettuato");
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  // Update nested model field (for flat sections like business_metrics, sales_pipeline, cash_funding)
  const updateField = (section, field, value) => {
    setModel(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Update per-year cost field (for sections with year1/year2/year3 sub-objects)
  const updateYearField = (section, year, field, value) => {
    setModel(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [year]: {
          ...prev[section][year],
          [field]: value
        }
      }
    }));
  };

  // Update growth target
  const updateGrowthTarget = (year, field, value) => {
    setModel(prev => ({
      ...prev,
      growth_targets: {
        ...prev.growth_targets,
        [year]: {
          ...prev.growth_targets[year],
          [field]: value
        }
      }
    }));
  };

  // Copy year costs to other years
  const copyYearCosts = (section, fromYear) => {
    setModel(prev => {
      const source = prev[section][fromYear];
      return {
        ...prev,
        [section]: {
          year1: { ...source },
          year2: { ...source },
          year3: { ...source }
        }
      };
    });
    toast.success(`Valori ${fromYear} copiati su tutti gli anni`);
  };

  // Add custom operating cost
  const addCustomCost = () => {
    setModel(prev => ({
      ...prev,
      operating_costs: {
        ...prev.operating_costs,
        [costYear]: {
          ...prev.operating_costs[costYear],
          custom_items: [...(prev.operating_costs[costYear]?.custom_items || []), { name: "Nuovo costo", amount: 0 }]
        }
      }
    }));
  };

  // Update custom cost
  const updateCustomCost = (index, field, value) => {
    const newItems = [...(model.operating_costs[costYear]?.custom_items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    setModel(prev => ({
      ...prev,
      operating_costs: {
        ...prev.operating_costs,
        [costYear]: {
          ...prev.operating_costs[costYear],
          custom_items: newItems
        }
      }
    }));
  };

  // Remove custom cost
  const removeCustomCost = (index) => {
    setModel(prev => ({
      ...prev,
      operating_costs: {
        ...prev.operating_costs,
        [costYear]: {
          ...prev.operating_costs[costYear],
          custom_items: (prev.operating_costs[costYear]?.custom_items || []).filter((_, i) => i !== index)
        }
      }
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header border-b border-border/60">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/api/static/logo" alt="Aiclo" className="h-9" />
              <div>
                <h1 className="text-xl font-bold tracking-tight">Aiclo Financial Planner</h1>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Model Selector */}
              <div className="flex items-center gap-2 bg-muted/50 rounded-sm p-1">
                <Select
                  value={currentModelId || "new"}
                  onValueChange={(value) => {
                    if (value === "new") {
                      newModel();
                    } else {
                      loadModel(value);
                    }
                  }}
                >
                  <SelectTrigger className="w-[220px] border-0 bg-transparent h-9" data-testid="model-selector">
                    <SelectValue placeholder="Seleziona modello" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new" className="font-medium">
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Nuovo Modello
                      </div>
                    </SelectItem>
                    {savedModels.length > 0 && (
                      <>
                        <Separator className="my-1" />
                        <div className="px-2 py-1.5">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">I Tuoi Modelli</p>
                        </div>
                        {savedModels.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{m.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(m.created_at).toLocaleDateString('it-IT')}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Model Name Input (for renaming) */}
              <Input
                type="text"
                value={model.name}
                onChange={(e) => setModel(prev => ({ ...prev, name: e.target.value }))}
                className="w-40 font-medium h-9"
                placeholder="Nome modello"
                data-testid="model-name-input"
              />
              
              <Button onClick={saveModel} disabled={isSaving} className="gap-2 h-9" data-testid="save-model-btn">
                <Save className="h-4 w-4" />
                Salva
              </Button>
              
              {currentModelId && (
                <Button 
                  variant="outline" 
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => {
                    if (window.confirm('Sei sicuro di voler eliminare questo modello?')) {
                      deleteModel(currentModelId);
                    }
                  }}
                  data-testid="delete-current-model-btn"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
              
              <Button variant="outline" onClick={exportCSV} disabled={!currentModelId} className="gap-2 h-9" data-testid="export-csv-btn">
                <Download className="h-4 w-4" />
                CSV
              </Button>

              <Button variant="outline" onClick={exportPDF} disabled={!metrics || isExportingPdf} className="gap-2 h-9" data-testid="export-pdf-btn">
                {isExportingPdf ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                PDF
              </Button>

              <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    disabled={!currentModelId} 
                    className="gap-2 h-9"
                    data-testid="share-btn"
                  >
                    <Share2 className="h-4 w-4" />
                    Condividi
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" data-testid="share-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Share2 className="h-5 w-5" />
                      Condividi Modello
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-2">
                    {shareInfo?.is_shared ? (
                      <>
                        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <Link className="h-4 w-4 text-emerald-600 shrink-0" />
                          <span className="text-sm text-emerald-700 font-medium">Condivisione attiva</span>
                          {shareInfo?.has_password && (
                            <span className="ml-auto flex items-center gap-1 text-xs text-emerald-600">
                              <Lock className="h-3 w-3" /> Protetto
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Input 
                            value={shareUrl || ""} 
                            readOnly 
                            className="text-sm font-mono bg-muted"
                            data-testid="share-link-input"
                          />
                          <Button size="icon" variant="outline" onClick={copyShareLink} data-testid="copy-share-link-btn">
                            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <Button size="icon" variant="outline" onClick={() => window.open(shareUrl, '_blank')} data-testid="open-share-link-btn">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Chiunque abbia questo link potrà visualizzare il modello in sola lettura.
                        </p>
                        <Separator />
                        <Button 
                          variant="destructive" 
                          className="w-full gap-2"
                          onClick={revokeSharing}
                          disabled={sharingLoading}
                          data-testid="revoke-share-btn"
                        >
                          Revoca Condivisione
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          Crea un link pubblico per condividere questo modello finanziario. 
                          Chi riceve il link potrà solo visualizzare i dati, senza modificarli.
                        </p>
                        <div className="space-y-2">
                          <Label className="text-sm flex items-center gap-2">
                            <Lock className="h-3.5 w-3.5" />
                            Password (opzionale)
                          </Label>
                          <Input
                            type="password"
                            placeholder="Lascia vuoto per accesso libero"
                            value={sharePassword}
                            onChange={(e) => setSharePassword(e.target.value)}
                            data-testid="share-password-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Se inserisci una password, chi apre il link dovrà inserirla per visualizzare il modello.
                          </p>
                        </div>
                        <Button 
                          className="w-full gap-2"
                          onClick={enableSharing}
                          disabled={sharingLoading}
                          data-testid="enable-share-btn"
                        >
                          {sharingLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                          ) : (
                            <Link className="h-4 w-4" />
                          )}
                          Crea Link di Condivisione
                        </Button>
                      </>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* User Menu */}
              <NotificationsBell onOpen={(n) => navigate('/contracts', { state: { focus: { type: n.type, id: n.type === "red_alert" ? n.client_id : n.item_id } } })} />
              <Button variant="outline" className="gap-2 h-9" onClick={() => navigate('/contracts')} data-testid="goto-contracts-btn">
                <FileText className="h-4 w-4" />
                Contratti
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium hidden md:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid w-full max-w-2xl grid-cols-4 h-12" data-testid="main-tabs">
            <TabsTrigger value="dashboard" className="gap-2 text-sm">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="business" className="gap-2 text-sm">
              <Building2 className="h-4 w-4" />
              Business
            </TabsTrigger>
            <TabsTrigger value="sales" className="gap-2 text-sm">
              <Target className="h-4 w-4" />
              Vendite
            </TabsTrigger>
            <TabsTrigger value="costs" className="gap-2 text-sm">
              <DollarSign className="h-4 w-4" />
              Costi
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-8" data-testid="dashboard-tab">
            {/* Year Selector */}
            <div className="flex items-center gap-2" data-testid="dashboard-year-selector">
              <span className="text-sm font-medium text-muted-foreground mr-2">Vista:</span>
              {[
                { key: "year1", label: "Anno 1" },
                { key: "year2", label: "Anno 2" },
                { key: "year3", label: "Anno 3" },
                { key: "consolidated", label: "3 Anni" }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setDashboardYear(key)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all border ${
                    dashboardYear === key 
                      ? "bg-primary text-primary-foreground border-primary shadow-sm" 
                      : "bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground"
                  }`}
                  data-testid={`dashboard-year-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <MetricCard
                title="MRR"
                value={formatCurrency(ym?.basic?.mrr_input || 0)}
                subtitle={`Fine anno: ${formatCurrency(ym?.basic?.mrr_end || 0)}`}
                icon={TrendingUp}
                trend="up"
                trendValue={ym?.basic?.mrr_end > ym?.basic?.mrr_input ? "crescita" : ""}
                className="animate-fade-in stagger-1"
              />
              <MetricCard
                title="Clienti"
                value={`${ym?.basic?.customers_start || 0} → ${ym?.basic?.customers_end || 0}`}
                subtitle={`ARPA: ${formatCurrency(ym?.basic?.arpa || 0)}`}
                icon={Users}
                className="animate-fade-in stagger-2"
              />
              <MetricCard
                title="Runway"
                value={`${ymRunway?.runway_months || 0} mesi`}
                subtitle={`Burn: ${formatCurrency(ymRunway?.monthly_burn_rate || 0)}/mese`}
                icon={PiggyBank}
                trend={ymRunway?.runway_months > 12 ? "up" : "down"}
                trendValue={ymRunway?.runway_months > 12 ? "Sano" : "Attenzione"}
                className="animate-fade-in stagger-3"
              />
              <MetricCard
                title="BEP"
                value={ymBep?.reached ? `Mese ${ymBep.month}` : "Non raggiunto"}
                subtitle={ymBep?.bep_mrr ? `MRR BEP: ${formatCurrency(ymBep.bep_mrr)}` : ""}
                icon={Target}
                trend={ymBep?.reached ? "up" : "down"}
                trendValue={ymBep?.reached ? "Raggiunto" : "Non raggiunto"}
                className="animate-fade-in stagger-4"
              />
              <MetricCard
                title="LTV/CAC"
                value={`${ym?.unit_economics?.ltv_cac_ratio?.toFixed(1) || 0}x`}
                subtitle={`CAC Payback: ${ym?.unit_economics?.cac_payback_months?.toFixed(1) || 0} mesi`}
                icon={Wallet}
                trend={ym?.unit_economics?.ltv_cac_ratio > 3 ? "up" : "down"}
                trendValue={ym?.unit_economics?.ltv_cac_ratio > 3 ? "Ottimo" : "Da migliorare"}
                className="animate-fade-in stagger-4"
              />
            </div>

            {/* P&L Summary Cards - TOTALS from projections */}
            {(() => {
              const totalRevenue = ymProjections.reduce((s, p) => s + p.revenue, 0);
              const totalVarCosts = ymProjections.reduce((s, p) => s + p.variable_costs, 0);
              const totalFixedCosts = ymProjections.reduce((s, p) => s + p.fixed_costs, 0);
              const totalProfit = ymProjections.reduce((s, p) => s + p.profit, 0);
              const periodLabel = dashboardYear === "consolidated" ? "3 Anni" : `Anno ${dashboardYear.replace("year","")}`;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="border-border/60 bg-emerald-50/50" data-testid="pnl-revenue-card">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Entrate Totali ({periodLabel})</p>
                          <p className="text-3xl font-bold font-data text-emerald-700 mt-2">{formatCurrency(totalRevenue)}</p>
                        </div>
                        <div className="p-3 bg-emerald-100 rounded-sm">
                          <TrendingUp className="h-6 w-6 text-emerald-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/60 bg-orange-50/50" data-testid="pnl-variable-costs-card">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Costi Variabili ({periodLabel})</p>
                          <p className="text-3xl font-bold font-data text-orange-600 mt-2">{formatCurrency(totalVarCosts)}</p>
                          <p className="text-xs text-muted-foreground mt-1">{totalRevenue > 0 ? Math.round(totalVarCosts / totalRevenue * 100) : 0}% delle entrate</p>
                        </div>
                        <div className="p-3 bg-orange-100 rounded-sm">
                          <ArrowDownRight className="h-6 w-6 text-orange-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/60 bg-red-50/50" data-testid="pnl-fixed-costs-card">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Costi Fissi ({periodLabel})</p>
                          <p className="text-3xl font-bold font-data text-red-600 mt-2">{formatCurrency(totalFixedCosts)}</p>
                          <p className="text-xs text-muted-foreground mt-1">{totalRevenue > 0 ? Math.round(totalFixedCosts / totalRevenue * 100) : 0}% delle entrate</p>
                        </div>
                        <div className="p-3 bg-red-100 rounded-sm">
                          <DollarSign className="h-6 w-6 text-red-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={`border-border/60 ${totalProfit >= 0 ? 'bg-emerald-50/50' : 'bg-red-50/50'}`} data-testid="pnl-net-profit-card">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Profitto Netto ({periodLabel})</p>
                          <p className={`text-3xl font-bold font-data mt-2 ${totalProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            {formatCurrency(totalProfit)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">{totalRevenue > 0 ? Math.round(totalProfit / totalRevenue * 100) : 0}% delle entrate</p>
                        </div>
                        <div className={`p-3 rounded-sm ${totalProfit >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          {totalProfit >= 0
                            ? <ArrowUpRight className="h-6 w-6 text-emerald-600" />
                            : <ArrowDownRight className="h-6 w-6 text-red-500" />
                          }
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })()}

            {/* P&L Chart - Full Width */}
            <Card className="card-hover border-border/60" data-testid="pnl-chart">
              <CardHeader>
                <CardTitle className="text-lg">Conto Economico - Proiezione 12 Mesi</CardTitle>
                <CardDescription>Entrate, Costi Variabili, Costi Fissi e Profitto/Perdita mensili</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={380}>
                  <ComposedChart data={ymProjections}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} tickFormatter={(v) => `Mese ${v}`} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (!active || !payload) return null;
                        const colorMap = { revenue: "#10B981", variable_costs: "#F59E0B", fixed_costs: "#EF4444", profit: "#064E3B" };
                        const labelMap = { revenue: "Entrate", variable_costs: "Costi Variabili", fixed_costs: "Costi Fissi", profit: "Profitto Netto" };
                        const order = ["revenue", "variable_costs", "fixed_costs", "profit"];
                        const sorted = [...payload].sort((a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey));
                        return (
                          <div style={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '4px', padding: '10px 14px', fontSize: '13px' }}>
                            <p style={{ fontWeight: 600, marginBottom: 6 }}>Mese {label}</p>
                            {sorted.map((entry) => (
                              <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                                <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: colorMap[entry.dataKey] || entry.color, flexShrink: 0 }} />
                                <span style={{ color: '#71717A' }}>{labelMap[entry.dataKey] || entry.name}:</span>
                                <span style={{ fontWeight: 600 }}>{formatCurrency(entry.value)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                    <Legend 
                      formatter={(value) => {
                        const labels = { 
                          revenue: "Entrate", 
                          variable_costs: "Costi Variabili", 
                          fixed_costs: "Costi Fissi", 
                          profit: "Profitto Netto" 
                        };
                        return labels[value] || value;
                      }}
                    />
                    <Bar dataKey="revenue" name="revenue" fill="#10B981" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="variable_costs" name="variable_costs" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="fixed_costs" name="fixed_costs" fill="#EF4444" radius={[2, 2, 0, 0]} />
                    <Line type="monotone" dataKey="profit" name="profit" stroke="#064E3B" strokeWidth={3} dot={{ fill: '#064E3B', strokeWidth: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* MRR Growth Chart */}
              <Card className="card-hover border-border/60" data-testid="mrr-growth-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Crescita MRR</CardTitle>
                  <CardDescription>Proiezione 12 mesi</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={ymProjections}>
                      <defs>
                        <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#064E3B" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#064E3B" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} tickFormatter={(v) => `M${v}`} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip 
                        formatter={(value) => [formatCurrency(value), "MRR"]}
                        labelFormatter={(label) => `Mese ${label}`}
                      />
                      <Area type="monotone" dataKey="mrr" stroke="#064E3B" strokeWidth={2} fill="url(#mrrGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Customer Growth Chart */}
              <Card className="card-hover border-border/60" data-testid="customer-growth-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Crescita Clienti</CardTitle>
                  <CardDescription>Proiezione 12 mesi</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={ymProjections}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} tickFormatter={(v) => `M${v}`} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => [value, "Clienti"]}
                        labelFormatter={(label) => `Mese ${label}`}
                      />
                      <Bar dataKey="customers" fill="#10B981" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Burn vs Revenue Chart */}
              <Card className="card-hover border-border/60" data-testid="burn-vs-revenue-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Gross Profit vs Costi Fissi</CardTitle>
                  <CardDescription>Confronto mensile - quando il Gross Profit supera i Costi Fissi, sei in profitto</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={ymProjections}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} tickFormatter={(v) => `M${v}`} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip 
                        formatter={(value, name) => {
                          const labels = { gross_profit: "Gross Profit", fixed_costs: "Costi Fissi", cash_remaining: "Cash Rimanente" };
                          return [formatCurrency(value), labels[name] || name];
                        }}
                        labelFormatter={(label) => `Mese ${label}`}
                      />
                      <Legend 
                        formatter={(value) => {
                          const labels = { gross_profit: "Gross Profit", fixed_costs: "Costi Fissi", cash_remaining: "Cash Rimanente" };
                          return labels[value] || value;
                        }}
                      />
                      <Bar dataKey="gross_profit" name="gross_profit" fill="#10B981" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="fixed_costs" name="fixed_costs" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                      <Line type="monotone" dataKey="cash_remaining" name="cash_remaining" stroke="#064E3B" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* CAC vs LTV Chart */}
              <Card className="card-hover border-border/60" data-testid="cac-ltv-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Unit Economics</CardTitle>
                  <CardDescription>CAC vs LTV</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6 py-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">CAC</span>
                        <span className="font-data font-medium">{formatCurrency(ym?.unit_economics?.cac || 0)}</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-amber-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, (ym?.unit_economics?.cac || 0) / (ym?.unit_economics?.ltv || 1) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">LTV</span>
                        <span className="font-data font-medium">{formatCurrency(ym?.unit_economics?.ltv || 0)}</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div className="text-center">
                        <p className="text-2xl font-bold font-data text-primary">{ym?.unit_economics?.ltv_cac_ratio?.toFixed(1) || 0}x</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">LTV/CAC Ratio</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold font-data text-primary">{ym?.unit_economics?.cac_payback_months?.toFixed(1) || 0}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Payback (mesi)</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pipeline Summary */}
            <Card className="card-hover border-border/60" data-testid="pipeline-summary">
              <CardHeader>
                <CardTitle className="text-lg">Pipeline Richiesta ({dashboardYear === "consolidated" ? "3 Anni" : `Anno ${dashboardYear.replace("year","")}`})</CardTitle>
                <CardDescription>Per raggiungere gli obiettivi di crescita</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 text-center p-4 bg-muted/50 rounded-sm">
                    <p className="text-3xl font-bold font-data text-primary">{formatNumber(ym?.pipeline?.leads_required || 0)}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Lead</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1 text-center p-4 bg-muted/50 rounded-sm">
                    <p className="text-3xl font-bold font-data text-primary">{formatNumber(ym?.pipeline?.discoveries_required || 0)}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Discovery</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1 text-center p-4 bg-muted/50 rounded-sm">
                    <p className="text-3xl font-bold font-data text-primary">{formatNumber(ym?.pipeline?.demos_required || 0)}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Demo</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1 text-center p-4 bg-secondary rounded-sm">
                    <p className="text-3xl font-bold font-data text-primary">{formatNumber(ym?.growth?.new_customers_to_acquire || 0)}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Nuovi Clienti</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Use of Funds Pie Chart */}
            {(() => {
              // Calculate fund allocation from fixed costs for selected period
              const getYearCosts = (yearKey) => {
                const st = model.sales_team[yearKey] || {};
                const mb = model.marketing_budget[yearKey] || {};
                const sm = model.sales_marketing_cost[yearKey] || {};
                const op = model.operating_costs[yearKey] || {};
                const ymData = metrics?.[yearKey];
                
                const salesTeamCost = (ymData?.sales_capacity?.total_sales_team_cost || 0) / 12;
                
                const goToMarket = (
                  (mb.cost_per_lead || 0) * ((ymData?.pipeline?.leads_required || 0) / 12) +
                  (mb.event_costs || 0) + (mb.agency_costs || 0) + (mb.team_salaries || 0) +
                  (sm.sales_salaries || 0) + (sm.head_of_sales_salary || 0) +
                  (sm.recruiting_costs || 0) + (sm.crm_tools || 0) + (sm.marketing_team_salaries || 0) +
                  salesTeamCost +
                  (op.customer_success || 0)
                ) * 12;
                
                const infrastructure = (op.ai_infrastructure || 0) * 12;
                const product = (op.engineering_team || 0) * 12;
                const operations = ((op.admin_legal || 0) + (op.office_tools || 0)) * 12 +
                  (op.custom_items || []).reduce((s, i) => s + (i.amount || 0), 0) * 12;
                
                return { goToMarket, infrastructure, product, operations };
              };
              
              let costs;
              if (dashboardYear === "consolidated") {
                const y1 = getYearCosts("year1");
                const y2 = getYearCosts("year2");
                const y3 = getYearCosts("year3");
                costs = {
                  goToMarket: y1.goToMarket + y2.goToMarket + y3.goToMarket,
                  infrastructure: y1.infrastructure + y2.infrastructure + y3.infrastructure,
                  product: y1.product + y2.product + y3.product,
                  operations: y1.operations + y2.operations + y3.operations,
                };
              } else {
                costs = getYearCosts(dashboardYear);
              }
              
              const total = costs.goToMarket + costs.infrastructure + costs.product + costs.operations;
              const pieData = [
                { name: "Go To Market", value: costs.goToMarket, color: "#10B981" },
                { name: "Infrastructure", value: costs.infrastructure, color: "#F59E0B" },
                { name: "Product", value: costs.product, color: "#3B82F6" },
                { name: "Operations", value: costs.operations, color: "#EF4444" },
              ].filter(d => d.value > 0);
              
              const periodLabel = dashboardYear === "consolidated" ? "3 Anni" : `Anno ${dashboardYear.replace("year","")}`;
              
              return (
                <Card className="card-hover border-border/60" data-testid="use-of-funds-chart">
                  <CardHeader>
                    <CardTitle className="text-lg">Use of Funds ({periodLabel})</CardTitle>
                    <CardDescription>Distribuzione dei costi fissi per categoria</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col lg:flex-row items-center gap-8">
                      <div className="w-full lg:w-1/2">
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={70}
                              outerRadius={120}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={index} fill={entry.color} stroke="white" strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.[0]) return null;
                                const d = payload[0].payload;
                                const pct = total > 0 ? Math.round(d.value / total * 100) : 0;
                                return (
                                  <div style={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '4px', padding: '10px 14px', fontSize: '13px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: d.color, flexShrink: 0 }} />
                                      <span style={{ fontWeight: 600 }}>{d.name}</span>
                                    </div>
                                    <p style={{ marginTop: 4 }}>{formatCurrency(d.value)} ({pct}%)</p>
                                  </div>
                                );
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="w-full lg:w-1/2 space-y-4">
                        {pieData.map((d) => {
                          const pct = total > 0 ? Math.round(d.value / total * 100) : 0;
                          return (
                            <div key={d.name} className="flex items-center gap-4">
                              <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                              <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm font-medium">{d.name}</span>
                                  <span className="text-sm font-data font-bold">{formatCurrency(d.value)}</span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: d.color }} />
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">{pct}% del totale</p>
                              </div>
                            </div>
                          );
                        })}
                        <div className="pt-3 border-t border-border">
                          <div className="flex justify-between text-sm font-medium">
                            <span>Totale Costi Fissi</span>
                            <span className="font-data font-bold">{formatCurrency(total)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </TabsContent>
          <TabsContent value="business" className="space-y-8" data-testid="business-tab">
            {/* Section 1: Business Metrics */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={Building2} 
                  title="Metriche Aziendali Attuali" 
                  description="Inserisci le metriche base del tuo business (clienti esistenti)"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <InputField
                    label="Clienti Attuali"
                    id="current-customers"
                    value={model.business_metrics.current_customers}
                    onChange={(v) => updateField('business_metrics', 'current_customers', v)}
                  />
                  <InputField
                    label="MRR Attuale"
                    id="current-mrr"
                    value={model.business_metrics.current_mrr}
                    onChange={(v) => updateField('business_metrics', 'current_mrr', v)}
                    prefix="€"
                  />
                  <InputField
                    label="Utenti Medi per Cliente"
                    id="avg-users"
                    value={model.business_metrics.avg_users_per_customer}
                    onChange={(v) => updateField('business_metrics', 'avg_users_per_customer', v)}
                  />
                  <InputField
                    label="Prezzo per Utente"
                    id="price-per-user"
                    value={model.business_metrics.price_per_user}
                    onChange={(v) => updateField('business_metrics', 'price_per_user', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Margine Lordo"
                    id="gross-margin"
                    value={model.business_metrics.gross_margin}
                    onChange={(v) => updateField('business_metrics', 'gross_margin', v)}
                    suffix="%"
                  />
                  <InputField
                    label="Churn Rate Annuale"
                    id="churn-rate"
                    value={model.business_metrics.annual_churn_rate}
                    onChange={(v) => updateField('business_metrics', 'annual_churn_rate', v)}
                    suffix="%"
                  />
                </div>
                
                {/* Calculated values */}
                <div className="mt-8 p-4 bg-secondary/50 rounded-sm">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">Valori Calcolati</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">ARPA</p>
                      <p className="text-xl font-bold font-data">{formatCurrency(metrics?.overview?.arpa || 0)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Utenti Totali</p>
                      <p className="text-xl font-bold font-data">{formatNumber(metrics?.overview?.total_users || 0)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">MRR Verificato</p>
                      <p className="text-xl font-bold font-data">{formatCurrency(metrics?.overview?.mrr_calculated || 0)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Growth Targets */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={Target} 
                  title="Obiettivi di Crescita" 
                  description="Definisci i target MRR per i prossimi 3 anni - i clienti vengono calcolati automaticamente"
                />
                
                <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                  <p className="text-sm text-blue-800">
                    <strong>Nota:</strong> I valori "Utenti per Account" e "Prezzo per Utente" si riferiscono ai <strong>nuovi clienti acquisiti</strong>. 
                    I clienti esistenti mantengono il loro ARPA attuale (€{formatNumber(metrics?.overview?.arpa || 0)}).
                  </p>
                </div>
                
                {['year1', 'year2', 'year3'].map((year, idx) => (
                  <div key={year} className="mb-8 last:mb-0">
                    <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      Anno {idx + 1}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pl-10">
                      <InputField
                        label="MRR Target"
                        id={`${year}-target-mrr`}
                        value={model.growth_targets[year].target_mrr}
                        onChange={(v) => updateGrowthTarget(year, 'target_mrr', v)}
                        prefix="€"
                      />
                      <InputField
                        label="Utenti/Account (nuovi clienti)"
                        id={`${year}-users-account`}
                        value={model.growth_targets[year].target_users_per_account}
                        onChange={(v) => updateGrowthTarget(year, 'target_users_per_account', v)}
                      />
                      <InputField
                        label="Prezzo/Utente (nuovi clienti)"
                        id={`${year}-price-user`}
                        value={model.growth_targets[year].target_price_per_user}
                        onChange={(v) => updateGrowthTarget(year, 'target_price_per_user', v)}
                        prefix="€"
                      />
                      <InputField
                        label="Margine Lordo"
                        id={`${year}-gross-margin`}
                        value={model.growth_targets[year].gross_margin}
                        onChange={(v) => updateGrowthTarget(year, 'gross_margin', v)}
                        suffix="%"
                      />
                    </div>
                    {metrics?.growth_all?.[year] && (
                      <div className="mt-4 pl-10 p-4 bg-muted/50 rounded-sm space-y-3">
                        {/* Calcolo dettagliato */}
                        <div className="text-sm border-b border-border/50 pb-3">
                          <p className="font-medium text-muted-foreground mb-2">Calcolo MRR:</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 font-data">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">MRR esistente ({metrics.growth_all[year].existing_customers} clienti):</span>
                              <span>{formatCurrency(metrics.growth_all[year].existing_mrr)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Gap da colmare:</span>
                              <span className="text-primary font-medium">{formatCurrency(metrics.growth_all[year].mrr_gap)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">ARPA nuovi clienti:</span>
                              <span>{formatCurrency(metrics.growth_all[year].target_arpa)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Nuovi clienti necessari:</span>
                              <span className="font-medium">{metrics.growth_all[year].new_customers_needed}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Risultati */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="text-center p-2 bg-background rounded">
                            <p className="text-2xl font-bold font-data text-primary">{metrics.growth_all[year].new_customers_to_acquire}</p>
                            <p className="text-xs text-muted-foreground">Nuovi Clienti (+ churn)</p>
                          </div>
                          <div className="text-center p-2 bg-background rounded">
                            <p className="text-2xl font-bold font-data">{metrics.growth_all[year].customers_required}</p>
                            <p className="text-xs text-muted-foreground">Clienti Totali Fine Anno</p>
                          </div>
                          <div className="text-center p-2 bg-background rounded">
                            <p className="text-2xl font-bold font-data text-emerald-600">{formatCurrency(metrics.growth_all[year].calculated_mrr)}</p>
                            <p className="text-xs text-muted-foreground">MRR Calcolato</p>
                          </div>
                          <div className="text-center p-2 bg-background rounded">
                            <p className="text-2xl font-bold font-data">{formatCurrency(metrics.growth_all[year].target_arr)}</p>
                            <p className="text-xs text-muted-foreground">ARR</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Section 8: Cash & Funding */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={PiggyBank} 
                  title="Cash e Funding" 
                  description="Gestisci la liquidità e gli investimenti"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <InputField
                    label="Cash Iniziale"
                    id="starting-cash"
                    value={model.cash_funding.starting_cash}
                    onChange={(v) => updateField('cash_funding', 'starting_cash', v)}
                    prefix="€"
                  />
                  <InputField
                    label="Investimento Ricevuto"
                    id="investment-amount"
                    value={model.cash_funding.investment_amount}
                    onChange={(v) => updateField('cash_funding', 'investment_amount', v)}
                    prefix="€"
                  />
                </div>
                
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sales Tab */}
          <TabsContent value="sales" className="space-y-8" data-testid="sales-tab">
            {/* Section 3: Sales Pipeline */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={Target} 
                  title="Modello Pipeline" 
                  description="Definisci i tassi di conversione del funnel"
                />
                <YearCostTabs value={costYear} onChange={setCostYear} onCopy={(yr) => copyYearCosts('sales_pipeline', yr)} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <InputField
                    label="Lead → Discovery"
                    id="lead-to-discovery"
                    value={model.sales_pipeline[costYear]?.lead_to_discovery}
                    onChange={(v) => updateYearField('sales_pipeline', costYear, 'lead_to_discovery', v)}
                    suffix="%"
                  />
                  <InputField
                    label="Discovery → Demo"
                    id="discovery-to-demo"
                    value={model.sales_pipeline[costYear]?.discovery_to_demo}
                    onChange={(v) => updateYearField('sales_pipeline', costYear, 'discovery_to_demo', v)}
                    suffix="%"
                  />
                  <InputField
                    label="Demo → Cliente"
                    id="demo-to-customer"
                    value={model.sales_pipeline[costYear]?.demo_to_customer}
                    onChange={(v) => updateYearField('sales_pipeline', costYear, 'demo_to_customer', v)}
                    suffix="%"
                  />
                  <InputField
                    label="Ciclo Vendita"
                    id="sales-cycle"
                    value={model.sales_pipeline[costYear]?.sales_cycle_months}
                    onChange={(v) => updateYearField('sales_pipeline', costYear, 'sales_cycle_months', v)}
                    suffix="mesi"
                  />
                </div>
                
                {cym?.pipeline && (
                  <div className="mt-8">
                    {model.sales_pipeline[costYear]?.sales_cycle_months > 0 && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                        <p className="text-sm text-blue-800">
                          <strong>Impatto Sales Cycle ({model.sales_pipeline[costYear]?.sales_cycle_months} mesi):</strong> I lead generati nel mese 1 diventeranno clienti paganti dal mese {(model.sales_pipeline[costYear]?.sales_cycle_months || 0) + 1}. 
                        </p>
                      </div>
                    )}
                    <div className="p-4 bg-secondary/50 rounded-sm">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">Pipeline Richiesta (Anno {costYear.replace("year","")})</p>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 text-center">
                          <p className="text-2xl font-bold font-data">{cym.pipeline.leads_required}</p>
                        <p className="text-xs text-muted-foreground">Lead</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 text-center">
                        <p className="text-2xl font-bold font-data">{cym.pipeline.discoveries_required}</p>
                        <p className="text-xs text-muted-foreground">Discovery</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 text-center">
                        <p className="text-2xl font-bold font-data">{cym.pipeline.demos_required}</p>
                        <p className="text-xs text-muted-foreground">Demo</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 text-center">
                        <p className="text-2xl font-bold font-data">{cym.growth?.new_customers_to_acquire || 0}</p>
                        <p className="text-xs text-muted-foreground">Clienti</p>
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section 4: Sales Team */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={Users} 
                  title="Team Vendite" 
                  description="Definisci quanti lead può gestire ogni venditore - il numero di sales rep viene calcolato automaticamente"
                />
                <YearCostTabs value={costYear} onChange={setCostYear} onCopy={(yr) => copyYearCosts('sales_team', yr)} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <InputField
                    label="Lead per Sales Rep (mese)"
                    id="leads-per-sales-rep"
                    value={model.sales_team[costYear]?.leads_per_sales_rep}
                    onChange={(v) => updateYearField('sales_team', costYear, 'leads_per_sales_rep', v)}
                  />
                  <InputField
                    label="Commissione per Utente"
                    id="commission-per-user"
                    value={model.sales_team[costYear]?.commission_per_user}
                    onChange={(v) => updateYearField('sales_team', costYear, 'commission_per_user', v)}
                    prefix="€"
                  />
                  <InputField
                    label="Costo Fisso per Rep"
                    id="fixed-cost-per-rep"
                    value={model.sales_team[costYear]?.fixed_cost_per_rep}
                    onChange={(v) => updateYearField('sales_team', costYear, 'fixed_cost_per_rep', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Tempo di Ramp-up"
                    id="ramp-time"
                    value={model.sales_team[costYear]?.ramp_time_months}
                    onChange={(v) => updateYearField('sales_team', costYear, 'ramp_time_months', v)}
                    suffix="mesi"
                  />
                </div>
                
                {cym?.sales_capacity && (
                  <div className="mt-8">
                    {model.sales_team[costYear]?.ramp_time_months > 0 && cym?.pipeline?.ramp_lead_increase_pct > 0 && (
                      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm">
                        <p className="text-sm text-amber-800">
                          <strong>Impatto Ramp Time ({model.sales_team[costYear]?.ramp_time_months} mesi):</strong> Per compensare la conversione ridotta durante l'onboarding, 
                          i lead sono aumentati del <strong>+{cym.pipeline.ramp_lead_increase_pct}%</strong> ({formatNumber(cym.pipeline.leads_required_base)} → {formatNumber(cym.pipeline.leads_required)} lead).
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-secondary rounded-sm text-center">
                        <p className="text-3xl font-bold font-data text-primary">{cym.sales_capacity.sales_reps_needed}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Sales Rep Necessari</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-sm text-center">
                        <p className="text-2xl font-bold font-data">{cym.sales_capacity.leads_per_rep_monthly}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Lead/Rep/Mese</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-sm text-center">
                        <p className="text-2xl font-bold font-data">{formatCurrency(cym.sales_capacity.total_commission_cost)}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Commissioni Totali</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-sm text-center">
                        <p className="text-2xl font-bold font-data">{formatCurrency(cym.sales_capacity.total_sales_team_cost)}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Costo Team Vendite</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section 5: Marketing Budget */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={Megaphone} 
                  title="Budget Marketing" 
                  description="Inserisci il CPL (Costo per Lead) - il budget variabile viene calcolato automaticamente"
                />
                <YearCostTabs value={costYear} onChange={setCostYear} onCopy={(yr) => copyYearCosts('marketing_budget', yr)} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <InputField
                    label="Costo per Lead (CPL)"
                    id="cost-per-lead"
                    value={model.marketing_budget[costYear]?.cost_per_lead}
                    onChange={(v) => updateYearField('marketing_budget', costYear, 'cost_per_lead', v)}
                    prefix="€"
                  />
                  <InputField
                    label="Eventi e Conferenze"
                    id="event-costs"
                    value={model.marketing_budget[costYear]?.event_costs}
                    onChange={(v) => updateYearField('marketing_budget', costYear, 'event_costs', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Costi Agenzia"
                    id="agency-costs"
                    value={model.marketing_budget[costYear]?.agency_costs}
                    onChange={(v) => updateYearField('marketing_budget', costYear, 'agency_costs', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Stipendi Team"
                    id="marketing-team-salaries"
                    value={model.marketing_budget[costYear]?.team_salaries}
                    onChange={(v) => updateYearField('marketing_budget', costYear, 'team_salaries', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                </div>
                
                {cym?.marketing && (
                  <div className="mt-8">
                    <div className="p-4 bg-secondary/50 rounded-sm mb-4">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Budget Variabile (CPL x Lead)</p>
                      <div className="flex items-center gap-4 text-lg">
                        <span className="font-data">{formatCurrency(cym.marketing.cost_per_lead)}</span>
                        <span className="text-muted-foreground">x</span>
                        <span className="font-data">{formatNumber(cym.marketing.leads_required)} lead</span>
                        <span className="text-muted-foreground">=</span>
                        <span className="font-data font-bold text-primary">{formatCurrency(cym.marketing.variable_marketing_budget)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-muted/50 rounded-sm text-center">
                        <p className="text-2xl font-bold font-data">{formatCurrency(cym.marketing.fixed_marketing_costs)}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Costi Fissi Annuali</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-sm text-center">
                        <p className="text-2xl font-bold font-data">{formatCurrency(cym.marketing.total_monthly_marketing)}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Totale Mensile</p>
                      </div>
                      <div className="p-4 bg-secondary rounded-sm text-center">
                        <p className="text-2xl font-bold font-data text-primary">{formatCurrency(cym.marketing.annual_marketing_budget)}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Budget Annuale Totale</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Costs Tab */}
          <TabsContent value="costs" className="space-y-8" data-testid="costs-tab">
            {/* Section 6: Sales & Marketing Cost */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={DollarSign} 
                  title="Costi Sales & Marketing" 
                  description="Costi fissi del team commerciale"
                />
                <YearCostTabs value={costYear} onChange={setCostYear} onCopy={(yr) => copyYearCosts('sales_marketing_cost', yr)} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <InputField
                    label="Stipendi Sales"
                    id="sales-salaries"
                    value={model.sales_marketing_cost[costYear]?.sales_salaries}
                    onChange={(v) => updateYearField('sales_marketing_cost', costYear, 'sales_salaries', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Stipendio Head of Sales"
                    id="head-sales-salary"
                    value={model.sales_marketing_cost[costYear]?.head_of_sales_salary}
                    onChange={(v) => updateYearField('sales_marketing_cost', costYear, 'head_of_sales_salary', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Costi Recruiting"
                    id="recruiting-costs"
                    value={model.sales_marketing_cost[costYear]?.recruiting_costs}
                    onChange={(v) => updateYearField('sales_marketing_cost', costYear, 'recruiting_costs', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="CRM & Tools"
                    id="crm-tools"
                    value={model.sales_marketing_cost[costYear]?.crm_tools}
                    onChange={(v) => updateYearField('sales_marketing_cost', costYear, 'crm_tools', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Stipendi Team Marketing"
                    id="marketing-salaries"
                    value={model.sales_marketing_cost[costYear]?.marketing_team_salaries}
                    onChange={(v) => updateYearField('sales_marketing_cost', costYear, 'marketing_team_salaries', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                </div>
                
                {cym?.cac && (
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <MetricCard
                      title="CAC Totale"
                      value={formatCurrency(cym.cac.fully_loaded_cac)}
                      icon={Calculator}
                    />
                    <MetricCard
                      title="CAC Marketing"
                      value={formatCurrency(cym.cac.marketing_cac)}
                      icon={Megaphone}
                    />
                    <MetricCard
                      title="CAC Sales"
                      value={formatCurrency(cym.cac.sales_cac)}
                      icon={Users}
                    />
                    <MetricCard
                      title="Costo Totale S&M"
                      value={formatCurrency(cym.cac.total_sales_marketing_cost)}
                      subtitle="annuale"
                      icon={DollarSign}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section 7: Operating Costs */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={Server} 
                  title="Costi Operativi" 
                  description="Costi fissi mensili dell'azienda"
                />
                <YearCostTabs value={costYear} onChange={setCostYear} onCopy={(yr) => copyYearCosts('operating_costs', yr)} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <InputField
                    label="Team Engineering"
                    id="engineering-team"
                    value={model.operating_costs[costYear]?.engineering_team}
                    onChange={(v) => updateYearField('operating_costs', costYear, 'engineering_team', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="AI & Cloud"
                    id="ai-infrastructure"
                    value={model.operating_costs[costYear]?.ai_infrastructure}
                    onChange={(v) => updateYearField('operating_costs', costYear, 'ai_infrastructure', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Customer Success"
                    id="customer-success"
                    value={model.operating_costs[costYear]?.customer_success}
                    onChange={(v) => updateYearField('operating_costs', costYear, 'customer_success', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Admin & Legale"
                    id="admin-legal"
                    value={model.operating_costs[costYear]?.admin_legal}
                    onChange={(v) => updateYearField('operating_costs', costYear, 'admin_legal', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                  <InputField
                    label="Ufficio & Tools"
                    id="office-tools"
                    value={model.operating_costs[costYear]?.office_tools}
                    onChange={(v) => updateYearField('operating_costs', costYear, 'office_tools', v)}
                    prefix="€"
                    suffix="/mese"
                  />
                </div>
                
                {/* Custom Items */}
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm font-medium">Voci Personalizzate</p>
                    <Button variant="outline" size="sm" onClick={addCustomCost} className="gap-2" data-testid="add-custom-cost-btn">
                      <Plus className="h-4 w-4" />
                      Aggiungi
                    </Button>
                  </div>
                  {(model.operating_costs[costYear]?.custom_items || []).map((item, index) => (
                    <div key={index} className="flex items-center gap-4 mb-3">
                      <Input
                        value={item.name}
                        onChange={(e) => updateCustomCost(index, 'name', e.target.value)}
                        className="flex-1"
                        placeholder="Nome voce"
                        data-testid={`custom-cost-name-${index}`}
                      />
                      <div className="relative w-40">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
                        <Input
                          type="number"
                          value={item.amount}
                          onChange={(e) => updateCustomCost(index, 'amount', parseFloat(e.target.value) || 0)}
                          className="pl-8 font-data"
                          data-testid={`custom-cost-amount-${index}`}
                        />
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeCustomCost(index)} data-testid={`remove-custom-cost-${index}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                {cym?.opex && (
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <MetricCard
                      title="OpEx Mensile"
                      value={formatCurrency(cym.opex.monthly_opex)}
                      icon={Briefcase}
                    />
                    <MetricCard
                      title="OpEx Annuale"
                      value={formatCurrency(cym.opex.annual_opex)}
                      icon={Briefcase}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section 9: Unit Economics Summary */}
            <Card className="border-border/60">
              <CardContent className="p-8">
                <SectionHeader 
                  icon={Calculator} 
                  title="Unit Economics SaaS" 
                  description="Metriche chiave del tuo business model"
                />
                {cym?.unit_economics && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                    <MetricCard
                      title="CAC"
                      value={formatCurrency(cym.unit_economics.cac)}
                      icon={DollarSign}
                    />
                    <MetricCard
                      title="LTV"
                      value={formatCurrency(cym.unit_economics.ltv)}
                      icon={TrendingUp}
                    />
                    <MetricCard
                      title="LTV/CAC"
                      value={`${cym.unit_economics.ltv_cac_ratio.toFixed(1)}x`}
                      icon={Target}
                      trend={cym.unit_economics.ltv_cac_ratio > 3 ? "up" : "down"}
                    />
                    <MetricCard
                      title="Payback"
                      value={`${cym.unit_economics.cac_payback_months.toFixed(1)} mesi`}
                      icon={PiggyBank}
                    />
                    <MetricCard
                      title="Gross Margin"
                      value={`${cym.unit_economics.gross_margin}%`}
                      icon={Wallet}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ============== APP ROUTER ==============
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/shared/:token" element={<SharedModelView />} />
      <Route path="/investor/:token" element={<SharedInvestorView />} />
      <Route path="/contracts" element={<ContractsPage />} />
      <Route path="/csm" element={<ContractsPage initialView="csm" />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/" element={<Dashboard />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
